import type { ColorByRule } from 'components/color-by-attribute-modal';

/**
 * The colour-by rule mechanics shared by every dataset in the display rail. A rule is
 * written over an attribute and derives its values from what is on the section, never a
 * fixed list — so adding a grid colours it without re-colouring anything already there.
 */

/** Anything a rule can colour: an id, and a way to read its attributes. */
export interface ColorableItem {
  id: string;
  attributes: Record<string, string | null>;
  /** A colour the item brings with it — a stored display colour — for defaults. */
  storedColor?: string | null;
}

/**
 * A rail rule, and whether its values are the section's or the user's. The rules a
 * section opens with follow the data, so adding a grid colours it too. Applying from the
 * modal makes them the user's list: re-deriving would put back what they just unticked.
 */
export interface SectionColorRule extends ColorByRule {
  derivedFromData?: boolean;
}

/**
 * The distinct values the items have for an attribute, in their own order — which is
 * the order the rule lists them and hands out default colours in.
 *
 * @param {Array} items - The items on the section.
 * @param {string} attribute - The attribute id.
 * @return {Array} The values, without duplicates or blanks.
 */
export const deriveValues = (
  items: ColorableItem[],
  attribute: string
): string[] => {
  const values: string[] = [];
  items.forEach((item) => {
    const value = item.attributes[attribute];
    if (value && !values.includes(value)) {
      values.push(value);
    }
  });
  return values;
};

/**
 * Brings a rule up to date with what is on the section: edited swatches kept, and a
 * deterministic default for anything new.
 *
 * Which values a rule covers depends on whose list it is. A rule that follows the
 * data takes every value the section has, so adding a grid colours it. A rule the
 * user has applied covers the values they ticked and no others — re-deriving those
 * would hand back the ones they had just unticked, and colour the plot by an
 * attribute they had narrowed on purpose. Either way a value the section no longer
 * has drops out, because a rule cannot colour what is not there.
 *
 * @param {SectionColorRule} rule - The rule as it stands.
 * @param {Array} items - The items on the section.
 * @param {Function} defaultColor - The default for the nth value; also handed the
 *   value itself, so a caller with a stored colour for it can prefer that.
 * @return {SectionColorRule} The rule, with its values and colours filled in.
 */
export const refreshRule = (
  rule: SectionColorRule,
  items: ColorableItem[],
  defaultColor: (index: number, value: string) => string
): SectionColorRule => {
  const derived = deriveValues(items, rule.attribute);
  const values = rule.derivedFromData
    ? derived
    : rule.selectedValues.filter((value) => derived.includes(value));
  const labelByValue = new Map(
    rule.selectedValues.map((value, index) => [
      value,
      rule.selectedValueLabels[index] ?? value,
    ])
  );
  const valueColors: Record<string, string> = {};
  values.forEach((value, index) => {
    valueColors[value] = rule.valueColors[value] ?? defaultColor(index, value);
  });
  return {
    ...rule,
    selectedValues: values,
    // The label the rule already carried, which the modal resolved from the value's
    // own name — the value itself only where there is nothing better.
    selectedValueLabels: values.map(
      (value) => labelByValue.get(value) ?? value
    ),
    valueColors,
  };
};

/**
 * The colour each item draws in.
 *
 * Rules are read in order and the last one with something to say wins, so a rule
 * added later layers over an earlier one. An item no rule covers falls back to the
 * default for its position, so nothing is ever left uncoloured — including an item
 * whose value the rule leaves out, which is the point of leaving it out.
 *
 * @param {Array} items - The items on the section, in their own order.
 * @param {Array} rules - The rail's rules for this dataset.
 * @param {string} target - The surface the rule has to be painting.
 * @param {Function} defaultColor - The fallback for the nth item; also handed the
 *   item itself, so a caller can prefer a colour the item carries of its own.
 * @return {Map} Item id to colour.
 */
export const resolveColors = (
  items: ColorableItem[],
  rules: ColorByRule[],
  target: string,
  defaultColor: (index: number, item: ColorableItem) => string
): Map<string, string> => {
  const colors = new Map<string, string>();

  items.forEach((item, index) => {
    colors.set(item.id, defaultColor(index, item));
  });

  rules
    .filter((rule) => rule.enabled && rule.applyTo[target])
    .forEach((rule) => {
      items.forEach((item) => {
        const value = item.attributes[rule.attribute];
        // A value the rule does not list is one the user unticked, so the item keeps
        // its default rather than being painted by a rule it is not part of.
        if (!value || !rule.selectedValues.includes(value)) {
          return;
        }
        const color = rule.valueColors[value];
        if (color) {
          colors.set(item.id, color);
        }
      });
    });

  return colors;
};
